import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Check, Loader2, XCircle, ArrowRight, MessageCircle, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const pollStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          setStatus('success');
          await checkAuth();
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts, checkAuth]);

  return (
    <div data-testid="payment-success-page" className="min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-amber-50 py-20">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md mx-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-10 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-6" />
              <h2 className="font-serif text-2xl text-slate-900 mb-2">Processing Payment...</h2>
              <p className="text-slate-500 text-sm">Please wait while we verify your payment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl text-slate-900 mb-2" data-testid="payment-success-message">Payment Successful!</h2>
              <p className="text-slate-500 text-sm mb-6">You are registered for the Heal And Manifest Masterclass!</p>
              <Button onClick={() => navigate('/webinar')} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white gap-2 w-full py-5 mb-3" data-testid="watch-webinar-btn">
                <Play className="w-5 h-5" /> Watch the Webinar Now
              </Button>
              <a
                href="https://chat.whatsapp.com/LbOL5Nm5UxZ0k8O7aokKm2"
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-4"
              >
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 w-full py-5" data-testid="success-whatsapp-group-btn">
                  <MessageCircle className="w-5 h-5" /> Join WhatsApp Group for Zoom Link
                </Button>
              </a>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full gap-2" data-testid="go-to-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="font-serif text-2xl text-slate-900 mb-2" data-testid="payment-error-message">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h2>
              <p className="text-slate-500 text-sm mb-8">
                {status === 'expired'
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue verifying your payment. If you were charged, please contact support.'}
              </p>
              <Button onClick={() => navigate('/courses')} variant="outline" className="rounded-full gap-2" data-testid="back-to-courses-btn">
                Back to Programs <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
